import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { logout, selectAuthCredentials } from '../modules/auth/store/auth-slice';
import type { RootState } from '../store/store';
import i18n from './i18n';
import { AUTH_SCOPE } from '../modules/auth/constants/auth-scope';

const mutex = new Mutex();

const baseApiQuery =
  (baseQuery: ReturnType<typeof fetchBaseQuery>): ReturnType<typeof fetchBaseQuery> =>
  async (args, api, extraOptions) => {
    await mutex.waitForUnlock();
    let result = await baseQuery(args, api, extraOptions);

    if (
      result.error?.status === 401 ||
      (result?.error && 'originalStatus' in result.error && result.error?.originalStatus === 401)
    ) {
      if (!mutex.isLocked()) {
        const state = api.getState() as RootState;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const authCredentials = selectAuthCredentials(state)!;
        const release = await mutex.acquire();

        try {
          const refreshResult = await baseQuery(
            {
              url: '/oauth/v2/token',
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: authCredentials.client_id,
                client_secret: authCredentials.client_secret,
                scope: AUTH_SCOPE,
              }),
            },
            api,
            extraOptions,
          );

          if (refreshResult.data) {
            result = await baseQuery(args, api, extraOptions);
          } else {
            api.dispatch(logout());
          }
        } finally {
          release();
        }
      } else {
        await mutex.waitForUnlock();
        result = await baseQuery(args, api, extraOptions);
      }
    }

    return result;
  };

const api = createApi({
  reducerPath: 'api',
  baseQuery: baseApiQuery(
    fetchBaseQuery({
      baseUrl: `${process.env.REACT_APP_API_URL}`,
      credentials: 'include',
      prepareHeaders: headers => {
        headers.set('Accept-Language', i18n.language);

        return headers;
      },
    }),
  ),
  endpoints: () => ({}),
});

export default api;
